/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { getAxios } from '@/lib/axios';
import { type AxiosInstance } from 'axios';
import { type QueryClient, type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import { type GcpVideo } from '../interface';
import { type Country } from '@/types';
import environmentConfig from '@/config/environment';

const GCP_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-product-data/v1/product-videos`;

const getGcpProductVideos = async (
  { queryKey }: QueryFunctionContext<ReturnType<typeof gcpProductVideosKeys>>,
  axiosInstance?: AxiosInstance,
) => {
  const [, { country, skuId }] = queryKey;

  if (skuId == null) {
    throw new Error("An 'skuId' wasn't passed");
  }

  const response = await getAxios(axiosInstance).get<GcpVideo[]>(
    `${GCP_URL}?country=${country}&skuId=${skuId}`,
    { withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS },
  );

  return response.data;
};

const gcpProductVideosKeys = ({ country, skuId }: { country: Country; skuId: string }) =>
  ['gcpProductVideos', { country, skuId }] as const;

export const useGcpProductVideos = ({
  country,
  skuId,
  enabled = false,
}: {
  country: Country;
  skuId: string;
  enabled?: boolean;
}) => {
  return useQuery({
    enabled: enabled,
    queryKey: gcpProductVideosKeys({ country, skuId }),
    queryFn: getGcpProductVideos,
  });
};

export const prefetchGcpProductVideos = async ({
  queryClient,
  country,
  skuId,
  axiosInstance,
}: {
  queryClient: QueryClient;
  country: Country;
  skuId: string;
  axiosInstance: AxiosInstance;
}) => {
  return queryClient.prefetchQuery({
    queryKey: gcpProductVideosKeys({ country, skuId }),
    queryFn: (context: QueryFunctionContext<ReturnType<typeof gcpProductVideosKeys>>) =>
      getGcpProductVideos(context, axiosInstance),
  });
};
